// Contract between client and server for sync: which tables, which mutations.

import { z } from "zod";

/** Tables the client keeps a copy of. Order matters for the client store (parents first). */
export const SYNC_TABLES = [
  "day_templates",
  "day_template_blocks",
  "day_template_rules",
  "day_overrides",
  "day_plans",
  "day_blocks",
  "tasks",
  "task_steps",
  "routines",
  "plan_entries",
  "routine_completions",
  "appointments",
  "appointment_exceptions",
  "checklist_items",
  "checklist_checks",
  "evening_sessions",
  "people",
  "meal_defaults",
  "meals",
  "meal_participants",
  "morning_steps",
  "task_categories",
  "weekly_reviews",
  "shop_categories",
  "products",
  "recipes",
  "shopping_items",
  "availability",
  "notes",
  "day_reflections",
  "important_dates",
  "shared_todos",
] as const;
export type SyncTable = (typeof SYNC_TABLES)[number];

export function isSyncTable(name: string): name is SyncTable {
  return (SYNC_TABLES as readonly string[]).includes(name);
}

const at = z.iso.datetime({ offset: true });
const date = z.iso.date();
const title = z.string().trim().min(1).max(500);
/** Local clock time "YYYY-MM-DDTHH:MM" in Europe/Amsterdam. */
const localDateTime = z.string().regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/);
const repeat = z
  .object({
    kind: z.enum(["none", "weekly", "monthly"]),
    interval: z.number().int().min(1).max(52),
    weekdays: z.array(z.number().int().min(1).max(7)).max(7),
    until: date.nullable(),
  })
  .refine((r) => r.kind !== "weekly" || r.weekdays.length > 0, "weekly needs weekdays");
const appointmentFields = {
  title: z.string().trim().min(1).max(300),
  location: z.string().trim().max(300).nullable(),
  notes: z.string().max(5000).nullable(),
  startLocal: localDateTime,
  durationMinutes: z.number().int().min(5).max(1440),
  repeat,
  remindMinutes: z.number().int().min(0).max(1440),
  /** What the other one sees of an own appointment (B43). */
  visibility: z.enum(["private", "busy", "shared"]).default("private"),
  /** One of the two of them together, in the shared agenda (B35). */
  together: z.boolean().default(false),
  /** Travel time: the notification says when to leave (golf 9). */
  travelMinutes: z.number().int().min(0).max(600).nullable().default(null),
};

const slotKind = z.enum(["routine", "sport", "care", "free", "flex", "meal", "social", "planning", "chores", "prep", "rest", "sleep"]);
const routineFields = z
  .object({
    id: z.uuid(),
    title: z.string().trim().min(1).max(200),
    scheduleType: z.enum(["fixed", "cadence", "occasional"]),
    weekdays: z.array(z.number().int().min(1).max(7)).max(7),
    timesPerWeek: z.number().int().min(1).max(7).nullable(),
    intervalDays: z.number().int().min(1).max(365).nullable(),
    slotKinds: z.array(slotKind).max(6),
    durationMinutes: z.number().int().min(5).max(240),
    weeklyTarget: z.number().int().min(1).max(7).nullable(),
    isOptional: z.boolean(),
    lastDoneOn: date.nullable(),
  })
  .refine((r) => r.scheduleType !== "fixed" || r.weekdays.length > 0, "fixed needs weekdays")
  .refine((r) => r.scheduleType !== "cadence" || r.timesPerWeek !== null, "cadence needs times per week")
  .refine((r) => r.scheduleType !== "occasional" || r.intervalDays !== null, "occasional needs an interval");

/** Mutations: one schema per name. The server validates, the client builds them with the same types. */
export const mutationArgs = {
  "dayBlock.setStatus": z.object({
    id: z.uuid(),
    status: z.enum(["open", "done", "skipped"]),
    at,
  }),
  /** New task in the inbox (snel vastleggen, delen). The id is made on the device. */
  "task.create": z.object({
    id: z.uuid(),
    title,
    notes: z.string().max(10000).nullable().optional(),
    source: z.enum(["app", "share"]).default("app"),
    /** Klusjes, Ideeën or one of PK's own (B32). */
    categoryId: z.uuid().nullable().optional(),
    /** Read by the AI after it arrives (AI golf A, B31). */
    ai: z.boolean().optional(),
    at,
  }),
  "task.update": z.object({
    id: z.uuid(),
    title: title.optional(),
    notes: z.string().max(10000).nullable().optional(),
    due_on: date.nullable().optional(),
    estimate_minutes: z
      .union([z.literal(15), z.literal(30), z.literal(60)])
      .nullable()
      .optional(),
    category_id: z.uuid().nullable().optional(),
    /** A reminder at this moment (AI golf A, also by hand). */
    remind_at: at.nullable().optional(),
  }),
  /**
   * Where a task goes without a day: inbox, list (open), Ooit (someday), Weg (cancelled), or done.
   * Its active plan entry, if any, is taken off the day (status unplanned), or done with it.
   */
  "task.setStatus": z.object({
    id: z.uuid(),
    status: z.enum(["inbox", "open", "someday", "done", "cancelled"]),
    at,
  }),
  /**
   * Plan a task on a date, in a block (or without block: "vandaag ergens").
   * A day that does not exist yet is created first. Planning on another day than the
   * current entry ends that entry (moved) and starts a new one that points back to it.
   */
  "task.plan": z.object({
    taskId: z.uuid(),
    entryId: z.uuid(),
    date,
    /** The block on that day; or, for a day not on the device yet, the block of its day type. */
    dayBlockId: z.uuid().nullable().optional(),
    templateBlockId: z.uuid().nullable().optional(),
    /** Why, for the move log (spec 5). */
    reason: z.enum(["planned", "nog_open_today", "nog_open_tomorrow"]).default("planned"),
  }),
  /** Check off (or undo) a task where it stands on Vandaag. */
  "planEntry.setDone": z.object({ id: z.uuid(), done: z.boolean(), at, completionId: z.uuid().optional() }),
  /** "Alles naar de lijst", "Opnieuw beginnen", "Welkom terug": these entries go off their day, the tasks back to the list. */
  "planEntries.unplan": z.object({
    ids: z.array(z.uuid()).min(1).max(500),
    reason: z.enum(["back_to_list", "all_to_list", "start_over", "welcome_back"]).default("back_to_list"),
  }),
  /** Undo of planEntries.unplan within a few seconds. */
  "planEntries.restore": z.object({ ids: z.array(z.uuid()).min(1).max(500) }),
  "step.add": z.object({ id: z.uuid(), taskId: z.uuid(), title: z.string().trim().min(1).max(300), position: z.number().int().min(0) }),
  "step.setChecked": z.object({ id: z.uuid(), checked: z.boolean(), at }),
  "step.remove": z.object({ id: z.uuid(), at }),
  /** New appointment, or the whole series changed. */
  "appointment.save": z.object({ id: z.uuid(), ...appointmentFields }),
  /** "Deze en volgende": the old series ends the day before, a new series starts with the changes. */
  "appointment.saveFrom": z.object({ id: z.uuid(), newId: z.uuid(), fromRecurrence: localDateTime, ...appointmentFields }),
  /** "Alleen deze keer": one occurrence changed. */
  "appointment.saveOccurrence": z.object({
    exceptionId: z.uuid(),
    appointmentId: z.uuid(),
    recurrenceLocal: localDateTime,
    startLocal: localDateTime,
    durationMinutes: z.number().int().min(5).max(1440),
    title: z.string().trim().min(1).max(300),
    location: z.string().trim().max(300).nullable(),
  }),
  "appointment.skipOccurrence": z.object({ exceptionId: z.uuid(), appointmentId: z.uuid(), recurrenceLocal: localDateTime }),
  "appointment.remove": z.object({ id: z.uuid(), at }),
  /** New routine, or changed: days the app may still change follow (L13). */
  "routine.save": routineFields,
  "routine.archive": z.object({ id: z.uuid(), at }),
  /** Checked off outside a day (routine list, "Nog deze week", af en toe). Done early takes the next time off (RO2 AC5). */
  "routine.complete": z.object({ id: z.uuid(), routineId: z.uuid(), date, at }),
  "routine.uncomplete": z.object({ id: z.uuid(), at }),
  /** "Opnieuw beginnen": these routines leave "Nog deze week" for the week of `week` (a Monday); null undoes it. */
  "routines.setDismissedWeek": z.object({ ids: z.array(z.uuid()).min(1).max(100), week: date.nullable() }),
  /**
   * Another day type for a date or period (Ziek, Vakantie, Vrij or any type): days that exist are rebuilt, from
   * today on (the past never changes). Ziek and Vakantie send the tasks of those days back to the list.
   */
  "day.setType": z.object({ id: z.uuid(), from: date, to: date, templateId: z.uuid(), at }),
  /** Back to the normal day type for a date or period; with undoId it is "Ongedaan maken" of that exception. */
  "day.clearType": z.object({ from: date, to: date, at, undoId: z.uuid().optional() }),
  /** Ritme editor: a day type, new or renamed, and the weekdays it is the standard for. */
  "template.save": z.object({
    id: z.uuid(),
    name: z.string().trim().min(1).max(80),
    weekdays: z.array(z.number().int().min(1).max(7)).max(7),
  }),
  "template.duplicate": z.object({ id: z.uuid(), newId: z.uuid(), name: z.string().trim().min(1).max(80) }),
  "template.archive": z.object({ id: z.uuid(), at }),
  "templateBlock.save": z.object({
    id: z.uuid(),
    templateId: z.uuid(),
    title: z.string().trim().min(1).max(120),
    slotKind: z.enum(["routine", "sport", "care", "work", "free", "flex", "meal", "social", "planning", "chores", "prep", "rest", "sleep"]),
    startLocal: z.string().regex(/^\d{2}:\d{2}$/),
    endLocal: z
      .string()
      .regex(/^\d{2}:\d{2}$/)
      .nullable(),
    groupTitle: z.string().trim().min(1).max(80).nullable(),
    isOptional: z.boolean(),
    isFixed: z.boolean(),
    notify: z.boolean(),
  }),
  "templateBlock.remove": z.object({ id: z.uuid(), at }),
  /** "Ook morgen en verder" after a change in the ritme editor: days of this type from `from` are rebuilt (R2 AC2). */
  "template.apply": z.object({ templateId: z.uuid(), from: date }),
  /**
   * A routine planned by PK on a day (af en toe in the evening planning, gym backup with its own time, RO3 AC2,
   * RO4 AC3/AC7). A routine with a week counter is never planned more than its week still needs (inv. 8).
   */
  "routine.planOn": z.object({
    entryId: z.uuid(),
    routineId: z.uuid(),
    date,
    dayBlockId: z.uuid().nullable(),
    atLocal: z
      .string()
      .regex(/^\d{2}:\d{2}$/)
      .nullable(),
    /** "Ruilen" (B28): a planned gym day of the same week that goes off for this one, so the week stays at its goal. */
    swapEntryId: z.uuid().optional(),
    /** The device's planning date: what counts as "still to come" this week (the device knows the day best). */
    today: date.optional(),
  }),
  "checklist.saveItem": z.object({
    id: z.uuid(),
    title: z.string().trim().min(1).max(120),
    condition: z.discriminatedUnion("kind", [
      z.object({ kind: z.literal("always") }),
      z.object({ kind: z.literal("sport"), titles: z.array(z.string().trim().min(1).max(120)).max(10).optional() }),
      z.object({ kind: z.literal("day_type"), template_id: z.uuid() }),
    ]),
    position: z.number().int().min(0).max(1000),
  }),
  "checklist.removeItem": z.object({ id: z.uuid(), at }),
  /** Klaarleggen: an item checked (or unchecked) for the day it is laid out for. */
  "checklist.check": z.object({ id: z.uuid(), itemId: z.uuid(), forDate: date, checked: z.boolean(), at }),
  /** Evening planning: started or moved to a step (AV1 AC3). */
  "evening.save": z.object({ id: z.uuid(), forDate: date, mode: z.enum(["evening", "quick"]), step: z.number().int().min(1).max(5), at }),
  /** Evening planning done: that day is planned and the app changes nothing there any more (AV1 AC2). */
  "evening.finish": z.object({
    id: z.uuid(),
    forDate: date,
    mode: z.enum(["evening", "quick"]),
    at,
    durationSeconds: z.number().int().min(0).max(86_400),
  }),
  /** Someone who eats along (Tsjits, a friend): new or renamed (E1). */
  "person.save": z.object({ id: z.uuid(), name: z.string().trim().min(1).max(80), position: z.number().int().min(0).max(1000) }),
  /** Removes a person; the standard forgets them, meals that were eaten keep them. */
  "person.remove": z.object({ id: z.uuid(), at }),
  /** Who eats along by default on a weekday (1 = Monday); open days that PK left alone follow (E1 AC2). */
  "mealDefaults.set": z.object({
    weekday: z.number().int().min(1).max(7),
    defaults: z.array(z.object({ id: z.uuid(), personId: z.uuid() })).max(20),
  }),
  /**
   * Who eats along on one day, or "Uit eten" (E1 AC2, AC3). The ids come from the device: the device's meal and
   * participants are the ones that stay, so no row lingers on the device that the server does not know.
   */
  "meal.set": z.object({
    id: z.uuid(),
    date,
    place: z.enum(["home", "out"]),
    participants: z.array(z.object({ id: z.uuid(), personId: z.uuid() })).max(20),
    at,
  }),
  /**
   * "Morgen geen gym" (avondplanning stap 2): the routine goes off that day; the week goal stays, so the weekend asks
   * to catch up (B24). Undo puts it back. A sport block with nothing else in it is skipped along.
   */
  "routineEntry.skip": z.object({ id: z.uuid(), skipped: z.boolean(), swapEntryId: z.uuid().optional(), today: date.optional() }),
  /** Meer > Ochtend and Meer > Avond (B27, B32): a step of a list, new or changed. Null template ids: every day type. */
  "morningStep.save": z.object({
    id: z.uuid(),
    part: z.enum(["morning", "evening"]).default("morning"),
    /** A step that never moves along with "Ik loop achter" (Bedtijd). */
    isFixed: z.boolean().default(false),
    title: z.string().trim().min(1).max(120),
    minutes: z.number().int().min(1).max(240),
    position: z.number().int().min(0).max(1000),
    templateIds: z.array(z.uuid()).min(1).max(50).nullable(),
    slotKind,
    notify: z.boolean(),
    /** The device's tomorrow: open days from there on follow the change (B27). */
    from: date.optional(),
  }),
  "morningStep.remove": z.object({ id: z.uuid(), at, from: date.optional() }),
  /** The order of the morning list, top to bottom. */
  "morningSteps.order": z.object({ ids: z.array(z.uuid()).min(1).max(100), from: date.optional() }),
  /** When the morning (or evening) starts on a day type; null: that day type has none. */
  "template.setMorningStart": z.object({
    templateId: z.uuid(),
    part: z.enum(["morning", "evening"]).default("morning"),
    start: z
      .string()
      .regex(/^\d{2}:\d{2}$/)
      .nullable(),
    from: date.optional(),
  }),
  /** A category of the task list, new or renamed (B32). */
  "category.save": z.object({ id: z.uuid(), name: z.string().trim().min(1).max(60), position: z.number().int().min(0).max(1000) }),
  /** A category goes; its tasks stay, without a category. */
  "category.remove": z.object({ id: z.uuid(), at }),
  /** Te controleren (B31): PK says the proposal is right, as it is or as he changed it. It is put in place. */
  "ai.accept": z.object({
    taskId: z.uuid(),
    proposal: z.object({
      kind: z.enum(["appointment", "task", "reminder", "date"]),
      confidence: z.number().min(0).max(1),
      title: z.string().trim().min(1).max(500),
      date: date.nullable(),
      time: z
        .string()
        .regex(/^\d{2}:\d{2}$/)
        .nullable(),
      durationMinutes: z.number().int().min(5).max(1440).nullable(),
      location: z.string().max(300).nullable(),
      category: z.string().max(60).nullable(),
      estimateMinutes: z.union([z.literal(15), z.literal(30), z.literal(60)]).nullable(),
      doubt: z.string().max(300).nullable(),
      birthday: z.boolean().nullable().optional(),
      someday: z.boolean().nullable().optional(),
    }),
  }),
  /** Te controleren: not this; it stays in the inbox as an ordinary item. */
  "ai.dismiss": z.object({ taskId: z.uuid() }),
  /** "Ongedaan maken" after the AI put something in place: it goes back to how it was, in the inbox. */
  "ai.undo": z.object({ taskId: z.uuid() }),
  /** Meer > Instellingen: AI on or off. */
  "settings.setAi": z.object({ on: z.boolean() }),
  /** The energy check in the morning (AI golf C). */
  "day.setEnergy": z.object({ date, energy: z.enum(["moe", "gewoon", "fit"]) }),
  /** The shared shopping list (fase 2): an item, its product (and category) remembered for next time. */
  "shop.add": z.object({
    id: z.uuid(),
    name: z.string().trim().min(1).max(120),
    amount: z.string().trim().max(60).nullable().optional(),
    categoryId: z.uuid().nullable().optional(),
    recipeId: z.uuid().nullable().optional(),
    at,
  }),
  "shop.check": z.object({ id: z.uuid(), checked: z.boolean(), at }),
  "shop.remove": z.object({ id: z.uuid(), at }),
  /** Done shopping: everything checked goes off the list. */
  "shop.clearChecked": z.object({ at }),
  /** An item in another category; the product remembers it. */
  "shop.setCategory": z.object({ id: z.uuid(), categoryId: z.uuid() }),
  /** A recipe (B38), by hand or from the AI. */
  "recipe.save": z.object({
    id: z.uuid(),
    name: z.string().trim().min(1).max(120),
    minutes: z.number().int().min(1).max(600).nullable(),
    servings: z.number().int().min(1).max(20).nullable(),
    ingredients: z.array(z.object({ name: z.string().trim().min(1).max(120), amount: z.string().trim().max(60).nullable() })).max(60),
    steps: z.array(z.string().trim().min(1).max(500)).max(40),
    notes: z.string().max(5000).nullable(),
    source: z.enum(["manual", "ai"]).default("manual"),
  }),
  "recipe.remove": z.object({ id: z.uuid(), at }),
  /** The week menu (B38): what is eaten on a day, from which recipe, and who cooks. The id is used when the meal is new. */
  "meal.setMenu": z.object({
    id: z.uuid(),
    date,
    dish: z.string().trim().max(120).nullable(),
    recipeId: z.uuid().nullable(),
    cookUserId: z.uuid().nullable(),
    cookPersonId: z.uuid().nullable(),
    at,
  }),
  /** Notities (B40). */
  "note.save": z.object({ id: z.uuid(), title: z.string().max(200), body: z.string().max(50000) }),
  "note.remove": z.object({ id: z.uuid(), at }),
  /** Meer > Instellingen: show Tsjits' busy times in the Week and the evening planning (B42). */
  "settings.setShowPartner": z.object({ on: z.boolean() }),
  /** "Hoe was je dag?" at Wind down (golf 9): one answer per day, can be changed. */
  "reflection.set": z.object({
    id: z.uuid(),
    day: date,
    mood: z.number().int().min(1).max(3),
    note: z.string().trim().max(500).nullable(),
  }),
  /** A birthday or other date that comes back every year (golf 9). */
  "importantDate.save": z
    .object({
      id: z.uuid(),
      title: z.string().trim().min(1).max(120),
      kind: z.enum(["birthday", "other"]),
      month: z.number().int().min(1).max(12),
      day: z.number().int().min(1).max(31),
      year: z.number().int().min(1900).max(2100).nullable(),
      remindDaysBefore: z.number().int().min(0).max(60),
      ideas: z.string().max(2000).nullable(),
    })
    .refine((d) => d.day <= [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][d.month - 1]!, "no such day"),
  "importantDate.remove": z.object({ id: z.uuid(), at }),
  /** Vaste boodschappen (golf 9): a product that is always bought, or not anymore. */
  "product.setStaple": z.object({ id: z.uuid(), on: z.boolean() }),
  /** The meal's own cook takes the dish the other one proposed (golf 9). */
  "meal.acceptDish": z.object({ id: z.uuid() }),
  /** Samen doen (B47): the list of the two of them. */
  "todo.add": z.object({
    id: z.uuid(),
    title: z.string().trim().min(1).max(200),
    note: z.string().trim().max(2000).nullable().optional(),
    at,
  }),
  "todo.check": z.object({ id: z.uuid(), done: z.boolean(), at }),
  "todo.remove": z.object({ id: z.uuid(), at }),
  "todo.clearDone": z.object({ at }),
  /** Meer > Instellingen: the rest screen after bedtime (B49). */
  "settings.setWindDown": z.object({ on: z.boolean() }),
  /** Meer > Instellingen: Ochtendstand on or off (V6 AC1). */
  "settings.setMorningMode": z.object({ on: z.boolean() }),
  /** "Ik loop achter": the rest of today's flexible blocks later, after PK confirmed (V5). */
  "dayBlocks.shift": z.object({ date, ids: z.array(z.uuid()).min(1).max(50), minutes: z.number().int().min(5).max(180) }),
  /** Notification on or off for a block of a day type; days from today on follow (spec 6). */
  "templateBlock.setNotify": z.object({ id: z.uuid(), notify: z.boolean(), from: date.optional() }),
} as const;

export type MutationName = keyof typeof mutationArgs;
export type MutationArgs<N extends MutationName> = z.input<(typeof mutationArgs)[N]>;
export type ParsedMutationArgs<N extends MutationName> = z.output<(typeof mutationArgs)[N]>;

export const mutationSchema = z.object({
  id: z.uuid(),
  name: z.string().min(1).max(64),
  args: z.unknown(),
});
export type Mutation = z.infer<typeof mutationSchema>;

export const pushBodySchema = z.object({
  deviceId: z.uuid(),
  mutations: z.array(mutationSchema).max(200),
});

export type MutationResult = { id: string; ok: true } | { id: string; ok: false; error: string };

export interface PullResponse {
  cursor: number;
  more: boolean;
  upserts: Partial<Record<SyncTable, Record<string, unknown>[]>>;
  removals: Partial<Record<SyncTable, string[]>>;
}
